import { motion } from 'framer-motion';
import { Bell, Sun, Moon, Sparkles } from 'lucide-react';
import logo from '../assets/logo.png';

interface MobileHeaderProps {
  onViewChange?: (view: string) => void;
  isDarkMode?: boolean;
  onDarkModeToggle?: () => void;
  onNotificationOpen?: () => void;
  subscriptionTier?: 'free' | 'pro' | 'max';
  unreadCount?: number;
}

export default function MobileHeader({ onViewChange, isDarkMode, onDarkModeToggle, onNotificationOpen, subscriptionTier, unreadCount = 0 }: MobileHeaderProps) {
  return (
    <header id="mobile-header" className="fixed top-0 left-0 right-0 h-16 bg-white/80 dark:bg-[#121212]/80 backdrop-blur-md border-b border-gray-100 dark:border-white/5 z-40 flex items-center justify-between px-4 lg:hidden transition-colors duration-300">
      {/* Left Logo */}
      <div className="flex items-center">
        <button
          onClick={() => onViewChange?.('চর্চাঘর')}
          className="hover:opacity-80 transition-opacity focus:outline-none cursor-pointer flex-shrink-0"
        >
          <img src={logo} alt="শুদ্ধশিক্ষা" className="w-[39px] h-[39px] rounded-lg object-cover" />
        </button>
      </div>

      {/* Right Actions: Dark Mode Toggle & Bell / Notification */}
      <div className="flex items-center gap-3">
        {onDarkModeToggle && (
          <button
            onClick={onDarkModeToggle}
            className="hidden relative flex items-center w-[72px] h-9 bg-gray-100 dark:bg-[#202020] border border-transparent dark:border-white/5 rounded-full p-1 cursor-pointer select-none focus:outline-none transition-colors duration-300 flex-shrink-0"
            aria-label="Toggle theme"
          >
            <motion.div
              className="absolute w-7 h-7 bg-white dark:bg-[#3A3A3C] rounded-full shadow-sm"
              animate={{ x: isDarkMode ? 36 : 0 }}
              transition={{ type: "spring", stiffness: 350, damping: 25 }}
            />
            <div className="relative z-10 w-7 h-7 flex items-center justify-center">
              <Sun className={`w-4.5 h-4.5 transition-colors duration-200 ${isDarkMode ? 'text-gray-500' : 'text-amber-500'}`} />
            </div>
            <div className="flex-1" />
            <div className="relative z-10 w-7 h-7 flex items-center justify-center">
              <Moon className={`w-4.5 h-4.5 transition-colors duration-200 ${isDarkMode ? 'text-white' : 'text-gray-400'}`} />
            </div>
          </button>
        )}

        {subscriptionTier === 'free' && (
          <button
            onClick={() => onViewChange?.('সাবস্ক্রিপশন')}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-[#0B57D0] hover:bg-[#0842A0] text-white font-bangla text-xs font-semibold rounded-full border border-blue-400/30 shadow-sm active:scale-95 transition-all cursor-pointer flex-shrink-0"
          >
            <Sparkles className="w-3.5 h-3.5 text-[#C2E7FF] fill-current" />
            আপগ্রেড
          </button>
        )}

        <div className="relative">
          <button
            onClick={onNotificationOpen}
            className="w-10 h-10 rounded-xl flex items-center justify-center text-gray-600 dark:text-[#A0A5AD] hover:bg-[#F0F1F5] dark:hover:bg-white/5 active:scale-95 transition-all cursor-pointer relative"
          >
            <Bell className="w-5 h-5" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-red-500 text-white text-[9px] font-black flex items-center justify-center rounded-full px-1 border-2 border-white dark:border-[#121212]">
                {unreadCount}
              </span>
            )}
          </button>
        </div>
      </div>
    </header>
  );
}
